import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import type { Industry } from '../../data/industries';
import { IndustryIcon } from './IndustryIcon';
type IndustrySpotlightProps = {
  industry: Industry;
};
export function IndustrySpotlight({
  industry
}: IndustrySpotlightProps) {
  return <motion.article key={industry.id} initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.22,
    ease: [0.23, 1, 0.32, 1]
  }} className="overflow-hidden rounded-2xl bg-brand-black text-white" aria-live="polite" data-id="element-542" data-mp="328inozt36yf5">
      <div className="p-8 sm:p-10" data-id="element-543" data-mp="2elsixqpcspqu">
        <div className="flex items-center gap-3" data-id="element-544" data-mp="2eio7q9dh0tqo">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-red" data-id="element-545" data-mp="2in4c267frt3s">
            <IndustryIcon name={industry.icon} className="h-5 w-5" data-id="element-546" data-mp="24ncs7ou8nihv" />
          </span>
          <p className="text-sm font-semibold text-brand-light" data-id="element-547" data-mp="29lxwq0giwoc4">{industry.name}</p>
        </div>
        <h3 className="mt-6 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl" data-id="element-548" data-mp="2d90r6ifrnrx7">{industry.headline}</h3>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-brand-light" data-id="element-549" data-mp="289e5rlpsb5db">{industry.summary}</p>
      </div>

      <div className="grid gap-8 border-t border-white/10 bg-[#0d0d0d] p-8 sm:p-10 md:grid-cols-2" data-id="element-550" data-mp="2m9ovumj9sfxf">
        <div data-id="element-551" data-mp="2hbnec61hsoo2">
          <p className="text-sm font-semibold text-white" data-id="element-552" data-mp="2hbx884pnrfz1">What we set up for you</p>
          <ul className="mt-5 space-y-4" data-id="element-553" data-mp="1tm2s9ea1lbak">
            {industry.systems.map(system => <li key={system} className="flex items-start gap-3 text-sm leading-relaxed text-brand-light" data-id="element-554" data-mp="1pl92sggybofk">
                <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-red" strokeWidth={3} aria-hidden="true" data-id="element-555" data-mp="23cxfakv44ex2" />
                {system}
              </li>)}
          </ul>
        </div>

        <div data-id="element-556" data-mp="1at8pdipp19mp">
          <p className="text-sm font-semibold text-white" data-id="element-557" data-mp="1asyzdv4nf2h0">What your customers see</p>
          <div className="mt-5 rounded-2xl bg-white p-4 text-brand-black shadow-lg" data-id="element-558" data-mp="15voztmnjpafg">
            <div className="flex items-center justify-between border-b border-brand-light pb-3" data-id="element-559" data-mp="35xotkef8mk5w">
              <span className="text-xs font-bold" data-id="element-560" data-mp="3avqc8y8r4hgu">{industry.sampleMessage.sender}</span>
              <span className="text-[11px] text-brand-gray" data-id="element-561" data-mp="3fpcfjzt0z8zz">now</span>
            </div>
            <motion.p key={industry.id} initial={{
            opacity: 0,
            scale: 0.96,
            y: 6
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 0.22,
            delay: 0.12,
            ease: [0.23, 1, 0.32, 1]
          }} className="mt-3 origin-bottom-left rounded-2xl rounded-bl-md bg-brand-light px-4 py-3 text-sm leading-relaxed" data-id="element-562" data-mp="3kne0yrdxbkem">
              {industry.sampleMessage.text}
            </motion.p>
            <p className="mt-2 text-[11px] text-brand-gray" data-id="element-563" data-mp="30wshveon9tpx">Sent automatically by AdROI</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-white/10 p-8 sm:flex-row sm:items-center sm:p-10" data-id="element-564" data-mp="g8czxnj25a2a">
        <a href="#/contact" className="group inline-flex items-center justify-center gap-2 rounded-md bg-brand-red px-6 py-3.5 text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-red-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-565" data-mp="20pvp29qltaft">
          Get the {industry.name} playbook
          <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-566" data-mp="1i20hx8phyuvo" />
        </a>
        <a href="#/pricing" className="inline-flex items-center justify-center rounded-md px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-567" data-mp="18zhhev96oucl">
          See plans & pricing
        </a>
      </div>
    </motion.article>;
}